import React, { useState, useEffect, useCallback } from 'react';
import { Save, Award, CheckCircle, Loader2, ChevronLeft, Send, Lock, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../api';

const gradeColors = {
  A1: 'badge-success', B2: 'badge-success', B3: 'badge-success',
  C4: 'badge-info', C5: 'badge-info', C6: 'badge-info',
  D7: 'badge-warning', E8: 'badge-warning', F9: 'badge-neutral',
};

const STATUS_BADGE = {
  draft:     { label: 'Draft',          cls: 'bg-slate-100 text-slate-600 border border-slate-200' },
  submitted: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  approved:  { label: 'Approved',       cls: 'bg-green-100 text-green-700 border border-green-200' },
};

function ResultStatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function TeacherResultsPage() {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);

  // Load teacher's assigned classes and subjects, then fetch exams
  useEffect(() => {
    api.get('/academics/my-assignments').then(r => {
      const assignments = r.data || [];
      const seen = new Set();
      const classes = [];
      const subjSet = new Set();
      for (const a of assignments) {
        if (!seen.has(a.class_id)) {
          seen.add(a.class_id);
          classes.push({ id: a.class_id, name: a.class_name });
        }
        if (a.subject_name) subjSet.add(a.subject_name);
      }
      setAssignedClasses(classes);
      setAllSubjects([...subjSet].sort());
    }).catch(() => {});
    fetchExams('', '');
  }, []);

  const fetchExams = async (classId, subjectName) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (classId)    params.set('class_id', classId);
      if (subjectName) params.set('subject_name', subjectName);
      const res = await api.get(`/exams?${params.toString()}`);
      setExams(res.data || []);
    } catch { toast.error('Failed to load exams'); }
    finally { setLoading(false); }
  };

  const openExam = async (exam) => {
    setSelectedExam(exam);
    setLoading(true);
    try {
      const res = await api.get(`/exams/${exam.id}/scores`);
      setScores((res.data || []).map(s => ({ ...s, inputScore: s.score !== null ? String(s.score) : '' })));
    } catch { toast.error('Failed to load student roster'); }
    finally { setLoading(false); }
  };

  const updateScore = (studentId, val) => {
    setScores(prev => prev.map(s => s.student_id === studentId ? { ...s, inputScore: val } : s));
  };

  const saveScores = async () => {
    const toSave = scores
      .filter(s => s.inputScore !== '' && !isNaN(Number(s.inputScore)))
      .map(s => ({ student_id: s.student_id, score: Number(s.inputScore) }));
    if (toSave.length === 0) { toast.error('No scores to save'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/exams/${selectedExam.id}/scores`, { scores: toSave });
      toast.success(`${res.saved} scores saved!`);
      const r = await api.get(`/exams/${selectedExam.id}/scores`);
      setScores((r.data || []).map(s => ({ ...s, inputScore: s.score !== null ? String(s.score) : '' })));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save scores'); }
    finally { setSaving(false); }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await api.post(`/exams/${selectedExam.id}/submit-results`, {});
      toast.success('Results submitted for admin approval!');
      // Update local state
      setSelectedExam(prev => ({ ...prev, result_status: 'submitted' }));
      setExams(prev => prev.map(e => e.id === selectedExam.id ? { ...e, result_status: 'submitted' } : e));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const isLocked = selectedExam && ['submitted', 'approved'].includes(selectedExam.result_status);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="teacher-results-page">
      {selectedExam ? (
        <>
          {/* Score Entry / View Header */}
          <div className="flex items-center gap-4 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => setSelectedExam(null)} className="text-slate-500 hover:text-slate-700 p-2" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-manrope text-xl font-bold text-slate-900">{selectedExam.title}</h1>
                <ResultStatusBadge status={selectedExam.result_status} />
              </div>
              <p className="text-slate-500 text-sm">{selectedExam.subject_name} • {selectedExam.class_name} • Max: {selectedExam.max_score}</p>
            </div>
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
              {!isLocked && (
                <Button onClick={saveScores} disabled={saving} className="bg-[#2563eb] hover:bg-blue-700 text-white" data-testid="save-scores-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Scores</>}
                </Button>
              )}
              {!isLocked && (
                <Button onClick={submitForReview} disabled={submitting} variant="outline" className="border-green-500 text-green-700 hover:bg-green-50" data-testid="submit-results-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Send className="w-4 h-4 mr-2" />Submit for Review</>}
                </Button>
              )}
            </div>
          </div>

          {/* Status notice */}
          {selectedExam.result_status === 'submitted' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="pending-review-notice">
              <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">Results are <strong>pending admin review</strong>. Score editing is locked. Contact your admin to reopen if changes are needed.</p>
            </div>
          )}
          {selectedExam.result_status === 'approved' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="approved-notice">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">Results are <strong>approved and published</strong>. Students can now view their scores.</p>
            </div>
          )}

          <div className="edu-card overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{scores.length} Students</p>
              <p className="text-xs text-slate-400">
                {isLocked ? 'View only — results locked' : `Enter marks out of ${selectedExam.max_score}`}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="edu-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>Admission No.</th>
                    <th>Score / {selectedExam.max_score}</th>
                    <th>Grade</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}><td colSpan={6}><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
                    ))
                  ) : scores.map((s, i) => (
                    <tr key={s.student_id}>
                      <td className="text-slate-400 font-medium">{i + 1}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-[10px]">
                            {s.student_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900">{s.student_name}</span>
                        </div>
                      </td>
                      <td className="text-slate-500 text-sm">{s.admission_number}</td>
                      <td>
                        {isLocked ? (
                          <span className="font-semibold text-slate-700">{s.score !== null ? s.score : '—'}</span>
                        ) : (
                          <Input
                            type="number"
                            value={s.inputScore}
                            onChange={e => updateScore(s.student_id, e.target.value)}
                            min={0}
                            max={selectedExam.max_score}
                            step={0.5}
                            placeholder="—"
                            className="h-8 w-24 text-center text-sm font-medium"
                            data-testid={`score-${s.student_id}`}
                          />
                        )}
                      </td>
                      <td>
                        {s.grade
                          ? <Badge className={`${gradeColors[s.grade] || 'badge-neutral'} text-xs font-bold`}>{s.grade}</Badge>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>
                      <td className="text-slate-500 text-sm">{s.remark || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Exam List */}
          <div>
            <h1 className="font-manrope text-2xl font-bold text-slate-900">Enter Results</h1>
            <p className="text-slate-500 text-sm mt-0.5">Select an exam to enter or view student scores</p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <Select
              value={filterClass || 'all'}
              onValueChange={v => { const val = v === 'all' ? '' : v; setFilterClass(val); fetchExams(val, filterSubject); }}
            >
              <SelectTrigger className="w-44 h-9 text-sm" data-testid="class-filter">
                <SelectValue placeholder="All My Classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All My Classes</SelectItem>
                {assignedClasses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {allSubjects.length > 0 && (
              <Select
                value={filterSubject || 'all'}
                onValueChange={v => { const val = v === 'all' ? '' : v; setFilterSubject(val); fetchExams(filterClass, val); }}
              >
                <SelectTrigger className="w-44 h-9 text-sm" data-testid="subject-filter">
                  <SelectValue placeholder="All My Subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All My Subjects</SelectItem>
                  {allSubjects.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : exams.length === 0 ? (
            <div className="edu-card py-16 text-center">
              <Award className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold text-slate-700">No exams found</p>
              <p className="text-sm text-slate-400 mt-1">
                {assignedClasses.length === 0
                  ? 'You have no class/subject assignments yet. Contact your admin.'
                  : 'Ask your school admin to create exam records for your subjects.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="exams-list">
              {exams.map(e => (
                <div
                  key={e.id}
                  className="edu-card p-5 flex items-center justify-between gap-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openExam(e)}
                  data-testid={`exam-card-${e.id}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      e.result_status === 'approved' ? 'bg-green-100' :
                      e.result_status === 'submitted' ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      {e.result_status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : e.result_status === 'submitted' ? (
                        <Eye className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Award className="w-5 h-5 text-[#2563eb]" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{e.title}</p>
                        <ResultStatusBadge status={e.result_status} />
                      </div>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <Badge className="badge-info text-xs">{e.subject_name}</Badge>
                        <Badge className="badge-neutral text-xs">{e.class_name}</Badge>
                        <Badge className="badge-neutral text-xs">{e.exam_type}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">{e.score_count || 0} scored</p>
                      <p className="text-xs text-slate-400">Max: {e.max_score}</p>
                    </div>
                    {e.result_status === 'approved' && <CheckCircle className="w-4 h-4 text-green-500" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
